import { useTranslation } from '@jq4race/i18n';
import { LocationPicker } from '@jq4race/ui';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useState } from 'react';

import { getAuthBootOutcomeKind, getCurrentSession } from '../app/auth-state';
import { clearPersistedQueryCache, queryClient } from '../app/query-client';
import { OPERATOR_ROLE } from '../modules/auth/domain/entities/session';
import { resolveIdentity } from '../modules/auth/domain/resolve-identity';
import { useAuthState } from '../modules/auth/ui/auth-state-context';
import { useRedirectWhenSignedOut } from '../modules/auth/ui/use-redirect-on-sign-out';
import { useActiveLocation } from '../modules/location-context/hooks/use-active-location';
import { PushNotificationBanner } from '../modules/notifications/ui/components/PushNotificationBanner';
import {
  clearOfflineProfile,
  readOfflineProfile,
} from '../modules/offline/application/offline-profile-store';
import { getPendingOutboxCount } from '../modules/offline/application/sign-out-guard';
import { resolveAuthGate } from '../modules/offline/domain/offline-profile';
import { offlineDb } from '../modules/offline/infrastructure/dexie/offline-db';
import { DexieKvStore } from '../modules/offline/infrastructure/dexie-kv';
import { useForegroundDrainerTriggers } from '../modules/offline/ui/use-foreground-drainer-triggers';
import { useOfflineShellRecovery } from '../modules/offline/ui/use-offline-shell-recovery';
import { AppShell } from '../modules/shell/ui/AppShell';
import { EmptyState } from '../modules/shell/ui/EmptyState';
import { SignOutConfirmDialog } from '../modules/shell/ui/SignOutConfirmDialog';

const offlineKv = new DexieKvStore(offlineDb);

// Guard for the operator-only area of the PWA. The PWA is a separate origin
// from the Web Admin, so this layer normally only has to enforce two things:
//   1. There is a session (else → /login).
//   2. The session belongs to an operator (else → /login; non-operators
//      have no legitimate path here).
//
// Task 3.6 / spec "Offline session persistence" adds a third branch: no
// in-memory session (main.tsx's bootstrapSessionOutcome() failed) while
// bootstrap failed for a genuine NETWORK reason is NOT an auth failure. When
// a non-expired offline profile exists (design D11), render the shell from
// it instead of bouncing to a /login the device cannot reach. See
// resolveAuthGate() for the actual decision table (unit tested in
// offline-profile.spec.ts).
//
// Adversarial-review fixes (Slice 3, finding #2 + its own follow-up
// review): the gate reads getAuthBootOutcomeKind() (set once by main.tsx
// from bootstrapSessionOutcome()'s real result), NOT `navigator.onLine` —
// that flag is devtools-spoofable and never actually proved the refresh
// request failed for network reasons. A real 401/403 (or any other 4xx,
// kind:'unauthenticated') always redirects to /login, even if
// navigator.onLine happens to read false and a valid cached profile
// exists. Conversely, a fetch failure, the 8s boot timeout, or ANY 5xx/429
// (kind:'unreachable' — see packages/api-client's classifyBootstrapFailure)
// DOES unlock the offline-shell branch when a valid profile exists: for an
// operator reopening the app, a backend outage is exactly as unusable as
// having no signal at all.
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async () => {
    const session = getCurrentSession();
    if (session && session.user.role !== OPERATOR_ROLE) {
      throw redirect({ to: '/login' });
    }

    const offlineProfile = session ? null : await readOfflineProfile(offlineKv);
    const gate = resolveAuthGate({
      hasSession: session !== null,
      bootOutcomeKind: getAuthBootOutcomeKind(),
      profile: offlineProfile,
      now: new Date(),
    });

    if (gate === 'redirect-login') {
      throw redirect({ to: '/login' });
    }

    return { offlineProfile: gate === 'offline-shell' ? offlineProfile : null };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { t } = useTranslation('location-context');
  const { session, signOut } = useAuthState();
  const { offlineProfile } = Route.useRouteContext();
  const isOfflineShell = offlineProfile !== null;
  // Covers manual sign-out and any future/automatic path that clears the
  // session — see use-redirect-on-sign-out.ts. Suppressed in offline-shell
  // mode: there is deliberately no in-memory session there, and redirecting
  // to /login would defeat the entire point of task 3.6.
  useRedirectWhenSignedOut({ skip: isOfflineShell });
  const { isLoading, bootResult, locations, activeLocationId, selectLocation } =
    useActiveLocation();

  // pwa-offline-first Slice 6 (coordinator finding #4) — resolved ONCE here
  // (the only place with both the live session and the route's
  // offlineProfile in scope) and threaded down to AppShell → ConnectivityBadge
  // /BottomNav, and used directly below to mount the foreground drainer's
  // boot/online/periodic triggers (coordinator finding #1).
  //
  // Slice 7 adversarial review, CRITICAL fix — `hasSession` (`session !==
  // null`) is passed alongside `identity` so a same-org/user transition
  // FROM the offline profile TO a live session (recovered by
  // useOfflineShellRecovery below, without a page reload) still forces a
  // fresh drainer tick — see that hook's own dependency-array comment.
  const identity = resolveIdentity(session, offlineProfile);
  useForegroundDrainerTriggers(identity, session !== null);

  // Slice 7 adversarial review, CRITICAL fix — reopening the app offline and
  // later reconnecting WITHOUT a manual reload used to never sync the
  // outbox (see the hook's own doc comment for the full root-cause trace).
  // Only active while actually rendering the offline-shell.
  useOfflineShellRecovery(isOfflineShell);

  // Task 3.7 — warn/confirm before signing out with unsynced work, and
  // ALWAYS clear the non-secret offline profile + query cache on an
  // explicit sign-out (binding decision: "clear the profile on explicit
  // logout and when a different user logs in on the device"). The outbox
  // itself (offlineDb.outbox/blobs) is NEVER touched here — pending
  // check-ins must survive a sign-out so a same-org operator can still sync
  // them later (design D12).
  //
  // Adversarial-review fix (Slice 3, finding #1): `queryClient.clear()`
  // alone only clears the IN-MEMORY cache — the persisted bytes in the
  // Dexie `kv` table (`rq-cache`) are only overwritten LATER, asynchronously,
  // via the persister's cache-event subscription (a batched, best-effort
  // side effect, not a guarantee). clearPersistedQueryCache() explicitly and
  // synchronously purges those bytes so no stale persisted query response
  // can outlive this sign-out.
  //
  // pwa-offline-first Slice 6 (task 6.9) — `window.confirm` replaced with an
  // in-app dialog (SignOutConfirmDialog): iOS Safari's standalone (installed
  // PWA) display mode does not reliably render `window.confirm`'s native
  // chrome, so it could not be trusted as the ONLY gate before discarding
  // the session with pending work still queued.
  const [pendingSignOutCount, setPendingSignOutCount] = useState<number | null>(null);

  async function handleSignOut() {
    const pending = await getPendingOutboxCount(offlineDb);
    if (pending > 0) {
      setPendingSignOutCount(pending);
      return;
    }
    await finishSignOut();
  }

  async function finishSignOut() {
    setPendingSignOutCount(null);
    await clearOfflineProfile(offlineKv);
    queryClient.clear();
    await clearPersistedQueryCache();
    await signOut();
  }

  // ── Loading state ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <p className="jq-body-sm text-muted-foreground">{t('loadingLocations')}</p>
      </div>
    );
  }

  // ── Branch 1: 0 active locations — blocking message (FR-18) ─────────────
  if (bootResult?.action === 'block') {
    return (
      <div
        role="alert"
        className="flex min-h-dvh flex-col items-center justify-center bg-background px-6"
      >
        <div className="w-full max-w-md">
          <EmptyState
            icon={Building2}
            title={t('noLocations.title')}
            description={t('noLocations.description')}
          />
        </div>
      </div>
    );
  }

  // ── Branch 4: 2+ locations, no/stale cache — show picker (FR-17) ────────
  if (bootResult?.action === 'show-picker') {
    return (
      <div className="min-h-dvh bg-background">
        <LocationPicker
          locations={locations}
          selectedId={activeLocationId ?? undefined}
          forceSelect
          onSelect={selectLocation}
        />
      </div>
    );
  }

  // ── Branch 2 (auto-select) + Branch 3 (valid cache): render the app ──────
  // bootResult is 'auto-select' | 'proceed' at this point.
  const activeLocation = locations.find((l) => l.id === activeLocationId);

  return (
    <>
      <AppShell
        locationName={activeLocation?.name}
        userEmail={session?.user.email ?? offlineProfile?.email ?? undefined}
        onSignOut={() => void handleSignOut()}
        identity={identity}
      >
        <Outlet />
        <PushNotificationBanner />
      </AppShell>
      {pendingSignOutCount !== null && (
        <SignOutConfirmDialog
          pendingCount={pendingSignOutCount}
          onConfirm={() => void finishSignOut()}
          onCancel={() => setPendingSignOutCount(null)}
        />
      )}
    </>
  );
}
